import ApiService from './ApiService';
import { mapObjective } from './ObjectiveFeedService';
import type { RawObjective } from './ObjectiveFeedService';
import type { Objective, ObjectiveProgressDetail } from '@customTypes/objectiveFeed';

export interface SkillInsight {
  skill_name: string;
  strategy_summary: string;
}

export interface ObjectiveSuggestion {
  title: string;
  description: string;
  progress_detail?: ObjectiveProgressDetail;
  discovery_questions?: string[];
  kickoff_body?: string;
  skill_insights?: SkillInsight[];
  // Optional short pill shown on the picker card (e.g. "Start here") to
  // signal a low-pressure default for users unsure which objective to pick.
  badge?: string;
}

class ObjectiveService {
  async getObjectives(): Promise<Objective[]> {
    const { success, data } = await ApiService.get<RawObjective[]>('/objectives');
    if (success && data) {
      return data.map(mapObjective);
    }
    return [];
  }

  async createObjective(payload: { title: string; description?: string; progress_detail?: ObjectiveProgressDetail; skip_kickoff?: boolean; discovery_questions?: string[]; kickoff_body?: string; skill_insights?: { skill_name: string; strategy_summary: string }[] }): Promise<Objective | null> {
    const { success, data } = await ApiService.post<typeof payload, RawObjective>('/objectives', payload);
    if (success && data) {
      return mapObjective(data);
    }
    return null;
  }

  async updateObjective(id: string, payload: { title?: string; description?: string }): Promise<Objective | null> {
    const { success, data } = await ApiService.put<typeof payload, RawObjective>(`/objectives/${id}`, payload);
    if (success && data) {
      return mapObjective(data);
    }
    return null;
  }

  async deleteObjective(id: string): Promise<void> {
    const { success } = await ApiService.delete(`/objectives/${id}`);
    if (!success) {
      throw new Error('Failed to delete objective');
    }
  }

  async getSuggestions(count?: number): Promise<ObjectiveSuggestion[]> {
    const params = count != null ? `?count=${count}` : '';
    const { success, data } = await ApiService.get<{
      suggestions: ObjectiveSuggestion[];
    }>(`/objectives/suggestions${params}`);
    if (success && data?.suggestions) {
      return data.suggestions;
    }
    return [];
  }

  async refreshProgress(): Promise<{ taskId: string; status: string } | null> {
    const { success, data } = await ApiService.post<
      Record<string, never>,
      { task_id: string; status: string }
    >('/objectives/refresh-progress', {});
    if (success && data) {
      return { taskId: data.task_id, status: data.status };
    }
    return null;
  }

  async triggerHeartbeat(): Promise<{ taskId: string; status: string } | null> {
    const { success, data } = await ApiService.post<
      Record<string, never>,
      { task_id: string; status: string }
    >('/objectives/heartbeat', {});
    if (success && data) {
      return { taskId: data.task_id, status: data.status };
    }
    return null;
  }

  async triggerWeeklyLearnings(): Promise<{
    taskIds: string[];
    status: string;
    existingExperiences: number;
    attempting: number;
  } | null> {
    const { success, data } = await ApiService.post<
      Record<string, never>,
      {
        task_ids?: string[];
        status: string;
        existing_experiences: number;
        attempting: number;
      }
    >('/objectives/weekly-learnings', {});
    if (success && data) {
      return {
        taskIds: data.task_ids ?? [],
        status: data.status,
        existingExperiences: data.existing_experiences ?? 0,
        attempting: data.attempting ?? 0,
      };
    }
    return null;
  }

  async getScanInsights(text: string, source: 'summary' | 'brand'): Promise<string[]> {
    const { success, data } = await ApiService.post<
      { text: string; source: string },
      { insights: string[] }
    >('/objectives/scan-insights', { text, source });
    if (success && data?.insights) {
      return data.insights;
    }
    return [];
  }

  async classifyPosts(): Promise<number> {
    const { success, data } = await ApiService.post<
      Record<string, never>,
      { classified: number }
    >('/objectives/classify-posts', {});
    if (success && data) {
      return data.classified;
    }
    return 0;
  }
}

export default new ObjectiveService();
